/**
 * Folder permissions setup / renewal dialog.
 * Prompts for the Font Project Folder and Settings Folder after welcome,
 * and keeps a toolbar Link Folders control until both are granted.
 */
import tippy from 'tippy.js';
import { Logger } from './logger';
import { bindModalEscape } from './ui/modal-escape';
import { getTheme } from './tippy-utils';
import { isFileSystemAccessSupported } from './file-system-adapter';
import { pluginRegistry, DiskPlugin } from './filesystem-plugins';
import { settingsFolder } from './settings-folder';
const console = new Logger('FolderPermissions');
const PROJECT_INFO_HTML = `
    <p>The Font Project Folder is the Disk root in Files. Open and save font sources there — for example <code>.babelfont</code>, <code>.glyphs</code>, <code>.ufo</code>, and <code>.designspace</code>.</p>
    <p>Pick the parent folder that contains your projects, not a single font file. Counterpunch cannot see files outside this folder. Chrome may ask you to confirm access again after a browser restart.</p>
`;
const SETTINGS_INFO_HTML = `
    <p>The Settings Folder is app-wide, not per font project. Counterpunch creates three subfolders automatically if they are missing:</p>
    <ul>
        <li><strong>Scripts</strong> — reusable Python opened from the Script Editor and Tools → Run Python Script</li>
        <li><strong>Filters</strong> — glyph overview user filters</li>
        <li><strong>Plugins</strong> — installed plugin wheels such as Font Destinations</li>
    </ul>
    <p>A dedicated folder named Counterpunch is a good choice. Do not point Settings at the same folder as a font project.</p>
`;
export const FOLDER_KIND_ICON = {
    project: 'bookmark_manager',
    settings: 'folder_managed'
};
const FOLDER_KIND_CALLOUT_TITLE = {
    project: 'Project Folder Required',
    settings: 'Settings Folder Required'
};
const FOLDER_KIND_CALLOUT_MESSAGE = {
    project: 'Link a Font Project Folder so Counterpunch can open and save font sources on disk.',
    settings: 'Link a Settings Folder so Counterpunch can store filters, scripts, and plugins on disk.'
};
export const LINK_FOLDERS_BUTTON_LABEL = 'Link Folders';
export const FOLDER_ACCESS_UNAVAILABLE_COPY = {
    title: 'Folder Access Unavailable',
    intro: 'This browser cannot link folders on disk. Use <strong>Chrome/Chromium, Safari, or Edge</strong>. Counterpunch needs the File System Access directory picker. Brave turns that API off by default, and Firefox does not provide it, so Disk, the project folder, and the Settings Folder are unavailable here. Memory storage still works.'
};
function getHost() {
    const holder = window;
    if (!holder.__folderPermissionsHost) {
        holder.__folderPermissionsHost = {
            listenersBound: false,
            autoPrompted: false,
            autoPromptSettled: false,
            autoPromptAwaitingClose: false,
            opening: false,
            dialogOverlay: null,
            linkFolderButton: null,
            escapeBinding: null
        };
    }
    return holder.__folderPermissionsHost;
}
let cachedStatus = null;
let infoTippyInstances = [];
export function classifyFolderAccess(hasHandle, permission) {
    if (!hasHandle) {
        return 'missing';
    }
    if (permission === 'granted') {
        return 'ready';
    }
    return 'needsRenewal';
}
export function isFolderSetupComplete(status) {
    return (status.project.state === 'ready' && status.settings.state === 'ready');
}
export function isFolderSetupCompleteSync() {
    return !!cachedStatus && isFolderSetupComplete(cachedStatus);
}
export function isSettingsFolderReadySync() {
    if (cachedStatus) {
        return cachedStatus.settings.state === 'ready';
    }
    return settingsFolder.hasFolder();
}
function isAutomatedSession() {
    return !!window.isTestMode?.();
}
function getProjectAdapter() {
    const plugin = pluginRegistry.get('disk');
    if (!(plugin instanceof DiskPlugin)) {
        return null;
    }
    return plugin.getAdapter();
}
async function readFolderStatus(adapter) {
    if (!adapter) {
        return { state: 'missing', name: null };
    }
    if (!adapter.hasDirectory()) {
        await adapter.initialize();
    }
    const hasHandle = adapter.hasDirectory();
    const name = hasHandle ? adapter.getDirectoryName() || null : null;
    if (!hasHandle) {
        return { state: 'missing', name: null };
    }
    const permission = await adapter.checkPermission();
    return {
        state: classifyFolderAccess(true, permission),
        name
    };
}
export async function refreshFolderSetupStatus() {
    await settingsFolder.initialize();
    cachedStatus = {
        project: await readFolderStatus(getProjectAdapter()),
        settings: {
            state: await settingsFolder.getAccessState(),
            name: settingsFolder.getFolderName()
        }
    };
    updateLinkFolderButton();
    /**
     * Folder project/settings access snapshot after a status refresh.
     */
    window.dispatchEvent(new CustomEvent('folderSetupStatusChanged', { detail: cachedStatus }));
    return cachedStatus;
}
function destroyInfoTippys() {
    for (const instance of infoTippyInstances) {
        instance.destroy();
    }
    infoTippyInstances = [];
}
function bindFolderDialogEscape(overlay) {
    const host = getHost();
    host.escapeBinding?.release();
    host.escapeBinding = bindModalEscape(closeDialog, {
        isOpen: () => overlay.isConnected
    });
}
function settleFolderPermissionsAutoPrompt() {
    const host = getHost();
    if (host.autoPromptSettled) {
        return;
    }
    host.autoPromptSettled = true;
    host.autoPromptAwaitingClose = false;
    /**
     * First-launch folder prompt finished (shown and closed, or skipped because
     * setup is complete or File System Access is unavailable).
     */
    window.dispatchEvent(new CustomEvent('folderPermissionsAutoPromptSettled'));
}
function closeDialog() {
    const host = getHost();
    const shouldSettleAutoPrompt = host.autoPromptAwaitingClose;
    host.escapeBinding?.release();
    host.escapeBinding = null;
    destroyInfoTippys();
    host.dialogOverlay?.remove();
    host.dialogOverlay = null;
    void refreshFolderSetupStatus();
    /**
     * Folder permissions dialog was closed (Continue, Skip, Escape, or overlay).
     */
    window.dispatchEvent(new CustomEvent('folderPermissionsDialogClosed'));
    if (shouldSettleAutoPrompt) {
        settleFolderPermissionsAutoPrompt();
    }
}
function bindInfoButton(button, content, overlay) {
    if (!(button instanceof HTMLElement)) {
        return;
    }
    infoTippyInstances.push(tippy(button, {
        content: `<div class="info-popup-content">${content}</div>`,
        allowHTML: true,
        interactive: true,
        trigger: 'click',
        theme: getTheme(),
        appendTo: overlay,
        maxWidth: 360,
        placement: 'right'
    }));
}
function folderPathLabel(status) {
    if (status.state === 'ready' && status.name) {
        return status.name;
    }
    if (status.state === 'needsRenewal' && status.name) {
        return status.name;
    }
    return 'No folder selected yet';
}
function renderFolderSection(kind, title, bodyHtml, status, pickerAvailable) {
    const actionDisabled = pickerAvailable ? '' : ' disabled';
    const action = status.state === 'needsRenewal'
        ? `<button type="button" class="dialog-button dialog-button-primary" data-folder-action="renew" data-folder-kind="${kind}"${actionDisabled}>Restore Access</button>`
        : `<button type="button" class="dialog-button dialog-button-primary" data-folder-action="select" data-folder-kind="${kind}"${actionDisabled}>Select Folder</button>`;
    const stateNote = status.state === 'needsRenewal'
        ? '<p class="folder-permissions-status-note folder-permissions-renewal-message">Access expired — restore it to keep using this folder.</p>'
        : status.state === 'ready'
            ? '<p class="folder-permissions-status-note">Access granted.</p>'
            : '';
    return `
        <section class="folder-permissions-section">
            <div class="folder-permissions-section-header">
                <span class="material-symbols-outlined folder-permissions-section-icon" aria-hidden="true">${FOLDER_KIND_ICON[kind]}</span>
                <h4>${title}</h4>
                <button type="button" class="confirm-dialog-info-btn material-symbols-outlined" data-folder-info="${kind}" aria-label="About ${title}">info</button>
            </div>
            <div class="folder-permissions-section-body">${bodyHtml}</div>
            <div class="folder-permissions-folder-row">
                ${action}
                <span class="folder-permissions-path" title="${folderPathLabel(status)}">${folderPathLabel(status)}</span>
            </div>
            ${stateNote}
        </section>
    `;
}
export function getFolderPermissionsDialogCopy(status, pickerAvailable) {
    if (!pickerAvailable) {
        return FOLDER_ACCESS_UNAVAILABLE_COPY;
    }
    const needsRenewal = status.project.state === 'needsRenewal' ||
        status.settings.state === 'needsRenewal';
    const anyMissing = status.project.state === 'missing' ||
        status.settings.state === 'missing';
    if (needsRenewal && !anyMissing) {
        return {
            title: 'Restore Folder Access',
            intro: 'The browser has withdrawn write access to a folder you already linked. Restore access so Counterpunch can keep reading and writing those files. This confirms the existing folder; it does not pick a new one.'
        };
    }
    if (needsRenewal && anyMissing) {
        return {
            title: 'Set Up Folders',
            intro: 'This app runs inside a browser, so it needs access to two folders. One still needs to be chosen. Another already has a folder on file, but Chrome has withdrawn permission — restore that access without choosing a different folder.'
        };
    }
    return {
        title: 'Link Folders',
        intro: 'Since this app runs inside a browser, you need to grant access to two folders.'
    };
}
async function paintDialog() {
    const host = getHost();
    if (!host.dialogOverlay) {
        return;
    }
    const status = await refreshFolderSetupStatus();
    const pickerAvailable = isFileSystemAccessSupported();
    const copy = getFolderPermissionsDialogCopy(status, pickerAvailable);
    const complete = isFolderSetupComplete(status);
    const title = host.dialogOverlay.querySelector('#folder-permissions-title');
    const content = host.dialogOverlay.querySelector('.folder-permissions-content');
    const continueBtn = host.dialogOverlay.querySelector('[data-action="continue"]');
    if (title) {
        title.textContent = copy.title;
    }
    if (content) {
        const introClass = pickerAvailable
            ? status.project.state === 'needsRenewal' ||
                status.settings.state === 'needsRenewal'
                ? 'folder-permissions-renewal-message'
                : ''
            : 'folder-permissions-unavailable';
        content.innerHTML = `
            <p class="${introClass}">${copy.intro}</p>
            ${renderFolderSection('project', 'Font Project Folder', '<p>You store your fonts here. Select the root folder that contains all of your font project files.</p>', status.project, pickerAvailable)}
            ${renderFolderSection('settings', 'Settings Folder', '<p>The app stores your glyph filters, Python scripts, and downloaded plugins in this folder.</p>', status.settings, pickerAvailable)}
        `;
    }
    if (continueBtn) {
        continueBtn.disabled = !complete;
    }
    destroyInfoTippys();
    bindInfoButton(host.dialogOverlay.querySelector('[data-folder-info="project"]'), PROJECT_INFO_HTML, host.dialogOverlay);
    bindInfoButton(host.dialogOverlay.querySelector('[data-folder-info="settings"]'), SETTINGS_INFO_HTML, host.dialogOverlay);
}
function concealOverlayForNativePicker() {
    const overlay = getHost().dialogOverlay;
    if (!overlay) {
        return () => undefined;
    }
    overlay.classList.add('is-native-picker-open');
    overlay.setAttribute('aria-hidden', 'true');
    return () => {
        overlay.classList.remove('is-native-picker-open');
        overlay.removeAttribute('aria-hidden');
    };
}
async function selectProjectFolder() {
    const plugin = pluginRegistry.get('disk');
    if (!(plugin instanceof DiskPlugin)) {
        return false;
    }
    const success = await plugin.showSetupUI();
    if (!success) {
        return false;
    }
    // Use the bootstrap-registered bridge — do not dynamic-import file-browser
    // from this module. glyph-overview also bundles this dialog, and a dynamic
    // import would load a second Yjs copy (yjs#438).
    const applyFontsFolderSelection = window.applyFontsFolderSelection;
    if (!applyFontsFolderSelection) {
        console.error('applyFontsFolderSelection is not available; file-browser has not loaded');
        return false;
    }
    await applyFontsFolderSelection({ source: 'attach' });
    return true;
}
async function handleFolderAction(kind, action) {
    if (!isFileSystemAccessSupported()) {
        await paintDialog();
        return;
    }
    const restoreOverlay = concealOverlayForNativePicker();
    try {
        if (kind === 'project') {
            if (action === 'select') {
                await selectProjectFolder();
            }
            else {
                const plugin = pluginRegistry.get('disk');
                if (plugin instanceof DiskPlugin) {
                    const granted = await plugin.requestPermission();
                    if (granted) {
                        window.dispatchEvent(new CustomEvent('fontsFolderAccessChanged', {
                            detail: {
                                hasFontsFolderAccess: true,
                                source: 'renew'
                            }
                        }));
                    }
                }
            }
        }
        else if (action === 'select') {
            await settingsFolder.selectFolder();
        }
        else {
            await settingsFolder.renewPermission();
        }
    }
    catch (error) {
        console.error('Folder action failed:', error);
    }
    finally {
        restoreOverlay();
    }
    await paintDialog();
}
export async function openFolderPermissionsDialog() {
    const host = getHost();
    if (host.opening) {
        return;
    }
    const existingOverlay = document
        .querySelector('.folder-permissions-dialog')
        ?.closest('.info-popup-overlay');
    if (existingOverlay instanceof HTMLElement) {
        host.dialogOverlay = existingOverlay;
        bindFolderDialogEscape(existingOverlay);
        await paintDialog();
        return;
    }
    if (host.dialogOverlay) {
        bindFolderDialogEscape(host.dialogOverlay);
        await paintDialog();
        return;
    }
    host.opening = true;
    const overlay = document.createElement('div');
    overlay.className = 'info-popup-overlay folder-permissions-overlay';
    overlay.style.display = 'flex';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-labelledby', 'folder-permissions-title');
    overlay.innerHTML = `
        <div class="info-popup confirm-dialog folder-permissions-dialog">
            <div class="info-popup-header">
                <h3 id="folder-permissions-title">Link Folders</h3>
                <button type="button" class="info-popup-close confirm-dialog-close-btn" aria-label="Skip">
                    <span class="material-symbols-outlined">close</span>
                </button>
            </div>
            <div class="info-popup-content confirm-dialog-content folder-permissions-content"></div>
            <div class="confirm-dialog-actions folder-permissions-actions">
                <button type="button" class="dialog-button" data-action="skip">Skip</button>
                <button type="button" class="dialog-button dialog-button-primary" data-action="continue" disabled>Continue</button>
            </div>
        </div>
    `;
    document.body.appendChild(overlay);
    host.dialogOverlay = overlay;
    bindFolderDialogEscape(overlay);
    overlay.addEventListener('click', (event) => {
        if (event.target === overlay) {
            closeDialog();
        }
    });
    overlay
        .querySelector('.confirm-dialog-close-btn')
        ?.addEventListener('click', closeDialog);
    overlay
        .querySelector('[data-action="skip"]')
        ?.addEventListener('click', closeDialog);
    overlay
        .querySelector('[data-action="continue"]')
        ?.addEventListener('click', () => {
        if (cachedStatus && isFolderSetupComplete(cachedStatus)) {
            closeDialog();
        }
    });
    overlay.addEventListener('click', (event) => {
        const target = event.target?.closest('[data-folder-action]');
        if (!target) {
            return;
        }
        const kind = target.dataset.folderKind;
        const action = target.dataset.folderAction;
        if ((kind === 'project' || kind === 'settings') &&
            (action === 'select' || action === 'renew')) {
            event.preventDefault();
            void handleFolderAction(kind, action);
        }
    });
    try {
        await paintDialog();
    }
    finally {
        host.opening = false;
    }
}
function ensureLinkFolderButton() {
    const host = getHost();
    const existing = document.getElementById('link-folder-btn');
    if (existing instanceof HTMLButtonElement) {
        host.linkFolderButton = existing;
        return existing;
    }
    if (host.linkFolderButton) {
        return host.linkFolderButton;
    }
    const toolbarRight = document.querySelector('.toolbar-right');
    const settingsBtn = document.getElementById('settings-btn');
    const tourChip = document.getElementById('tour-launch-chip');
    if (!toolbarRight || !settingsBtn) {
        return null;
    }
    const button = document.createElement('button');
    button.type = 'button';
    button.id = 'link-folder-btn';
    button.className = 'toolbar-link-folder-btn';
    button.textContent = LINK_FOLDERS_BUTTON_LABEL;
    button.title = 'Link or restore project and settings folders';
    button.hidden = true;
    button.addEventListener('click', () => {
        void openFolderPermissionsDialog();
    });
    toolbarRight.insertBefore(button, tourChip || settingsBtn);
    host.linkFolderButton = button;
    return button;
}
function updateLinkFolderButton() {
    const button = ensureLinkFolderButton();
    if (!button) {
        return;
    }
    const show = !!cachedStatus && !isFolderSetupComplete(cachedStatus);
    button.hidden = !show;
}
function folderKindIconElement(kind, className) {
    const icon = document.createElement('span');
    icon.className = `material-symbols-outlined ${className}`;
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = FOLDER_KIND_ICON[kind];
    return icon;
}
export function createFolderSetupCallout(message, kind = 'settings') {
    const callout = document.createElement('div');
    callout.className = 'folder-setup-callout';
    const heading = document.createElement('div');
    heading.className = 'folder-setup-callout-heading';
    const title = document.createElement('h2');
    title.textContent = FOLDER_KIND_CALLOUT_TITLE[kind];
    heading.append(folderKindIconElement(kind, 'folder-setup-callout-icon'), title);
    const text = document.createElement('p');
    text.textContent = message || FOLDER_KIND_CALLOUT_MESSAGE[kind];
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'toolbar-link-folder-btn';
    button.textContent = LINK_FOLDERS_BUTTON_LABEL;
    button.addEventListener('click', () => {
        void openFolderPermissionsDialog();
    });
    callout.append(heading, text, button);
    return callout;
}
export function getFolderSetupCalloutHtml(kind = 'settings') {
    return `
        <div class="folder-setup-callout">
            <div class="folder-setup-callout-heading">
                <span class="material-symbols-outlined folder-setup-callout-icon" aria-hidden="true">${FOLDER_KIND_ICON[kind]}</span>
                <h2>${FOLDER_KIND_CALLOUT_TITLE[kind]}</h2>
            </div>
            <p>${FOLDER_KIND_CALLOUT_MESSAGE[kind]}</p>
            <button type="button" class="toolbar-link-folder-btn" data-action="link-folder">${LINK_FOLDERS_BUTTON_LABEL}</button>
        </div>
    `;
}
export async function maybeShowFolderPermissionsDialog() {
    const host = getHost();
    if (isAutomatedSession() || host.autoPrompted) {
        await refreshFolderSetupStatus();
        if (!host.autoPrompted) {
            host.autoPrompted = true;
            settleFolderPermissionsAutoPrompt();
        }
        return;
    }
    host.autoPrompted = true;
    await refreshFolderSetupStatus();
    if (!isFileSystemAccessSupported() ||
        !cachedStatus ||
        isFolderSetupComplete(cachedStatus)) {
        settleFolderPermissionsAutoPrompt();
        return;
    }
    host.autoPromptAwaitingClose = true;
    await openFolderPermissionsDialog();
}
function bindStatusListeners() {
    const host = getHost();
    if (host.listenersBound) {
        return;
    }
    host.listenersBound = true;
    window.addEventListener('welcomeScreenSettled', () => {
        void maybeShowFolderPermissionsDialog();
    });
    window.addEventListener('settingsFolderAccessChanged', () => {
        void refreshFolderSetupStatus();
    });
    window.addEventListener('fontsFolderAccessChanged', () => {
        void refreshFolderSetupStatus();
    });
    window.addEventListener('pluginFolderClosed', () => {
        void refreshFolderSetupStatus();
    });
}
bindStatusListeners();
